import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RotateCcw, Search } from "lucide-react";

export default function StorePricing() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(user?.role === "manager" ? user.store_id : null);
  const [items, setItems] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  // Admins + super_admins bypass; everyone else needs menu:manage
  const canEdit = ["admin", "super_admin"].includes(user?.role)
    || (user?.permissions?.menu === "manage");

  useEffect(() => {
    api.get("/stores").then((r) => {
      setStores(r.data);
      if (!storeId && r.data.length && ["admin","super_admin"].includes(user?.role)) setStoreId(r.data[0].id);
    });
  }, [storeId, user?.role]);

  const loadOverrides = useCallback(() => {
    if (!storeId) return;
    api.get(`/stores/${storeId}/pricing`).then((r) => {
      const m = {}; for (const o of r.data) m[o.menu_item_id] = o;
      setOverrides(m);
    });
  }, [storeId]);

  useEffect(() => {
    api.get("/menu").then((r) => setItems(r.data));
    loadOverrides();
  }, [loadOverrides]);

  const save = async (item, patch) => {
    if (!storeId || !canEdit) return;
    try {
      await api.put(`/stores/${storeId}/pricing`, { menu_item_id: item.id, ...patch });
      toast.success("Saved");
      loadOverrides();
    } catch { toast.error("Failed"); }
  };

  const clear = async (item) => {
    if (!canEdit) return;
    try {
      await api.delete(`/stores/${storeId}/pricing/${item.id}`);
      toast.success("Cleared override"); loadOverrides();
    } catch { toast.error("Failed"); }
  };

  const matchingItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const searchable = [item.name, item.category, item.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(query);
    });
  }, [items, searchQuery]);

  const grouped = useMemo(() => {
    const g = {};
    for (const it of matchingItems) {
      (g[it.category] = g[it.category] || []).push(it);
    }
    return g;
  }, [matchingItems]);

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-2">Per-Store Pricing</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="pricing-title">Store Pricing</h1>
          <p className="text-sm text-neutral-500 mt-1">Override price, availability, or stock per store. Blank = uses master menu price.</p>
        </div>
        {["admin","super_admin"].includes(user?.role) && (
          <div className="w-64">
            <Select value={storeId || ""} onValueChange={setStoreId}>
              <SelectTrigger data-testid="pricing-store-select"><SelectValue placeholder="Choose store"/></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {user?.role === "manager" && (
          <div className="text-sm text-neutral-500">Managing: <span className="font-semibold text-neutral-900">{stores.find(s => s.id === storeId)?.name || "—"}</span></div>
        )}
      </div>

      {!storeId ? (
        <div className="text-neutral-500">Select a store to manage pricing.</div>
      ) : (
        <div className="space-y-8">
          <div className="max-w-xl">
            <Label htmlFor="pricing-search" className="sr-only">Search menu items</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <Input
                id="pricing-search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search menu items, categories, or descriptions"
                className="h-11 bg-white pl-10"
                data-testid="pricing-search"
              />
            </div>
            <p className="mt-2 text-xs text-neutral-500" data-testid="pricing-search-count">
              {matchingItems.length} of {items.length} menu items
            </p>
          </div>
          {!canEdit && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-center gap-2" data-testid="pricing-readonly-banner">
              <span className="font-semibold">View only</span>
              <span className="text-amber-800">— you don't have permission to change pricing. Ask an admin for "Menu: Manage" access.</span>
            </div>
          )}
          {matchingItems.length === 0 && (
            <div
              className="rounded-xl border border-dashed border-neutral-300 bg-white px-6 py-10 text-center text-sm text-neutral-500"
              data-testid="pricing-no-results"
            >
              No menu items match your search.
            </div>
          )}
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
              <div className="px-6 py-3 border-b border-neutral-200 flex items-center gap-2">
                <h2 className="font-display font-semibold">{cat}</h2>
                <span className="text-xs text-neutral-400">({list.length})</span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-neutral-500 bg-neutral-50">
                  <tr><th className="p-3">Item</th><th className="p-3">Master $</th><th className="p-3">Store $</th><th className="p-3">Stock</th><th className="p-3">Available</th><th className="p-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {list.map((it) => {
                    const o = overrides[it.id];
                    return (
                      <tr key={it.id} data-testid={`pricing-row-${it.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <img src={it.image_url} alt="" className="w-10 h-10 rounded object-cover"/>
                            <span className="font-medium">{it.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-neutral-500">${it.price.toFixed(2)}</td>
                        <td className="p-3">
                          <Input
                            type="number" step="0.01"
                            defaultValue={o?.price ?? ""}
                            placeholder={`$${it.price.toFixed(2)}`}
                            disabled={!canEdit}
                            onBlur={(e) => {
                              const v = e.target.value;
                              if (v === "" && o?.price !== undefined) return; // no-op, allow explicit clear
                              if (v !== "") save(it, { price: parseFloat(v) });
                            }}
                            className="w-24 h-9 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                            data-testid={`pricing-price-${it.id}`}
                          />
                        </td>
                        <td className="p-3">
                          <Input
                            type="number"
                            defaultValue={o?.stock ?? ""}
                            placeholder={String(it.stock)}
                            disabled={!canEdit}
                            onBlur={(e) => {
                              const v = e.target.value;
                              if (v !== "") save(it, { stock: parseInt(v) });
                            }}
                            className="w-20 h-9 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                            data-testid={`pricing-stock-${it.id}`}
                          />
                        </td>
                        <td className="p-3">
                          <Switch
                            checked={o?.is_available ?? it.is_available}
                            disabled={!canEdit}
                            onCheckedChange={(v) => save(it, { is_available: v })}
                            data-testid={`pricing-available-${it.id}`}
                          />
                        </td>
                        <td className="p-3">
                          {o && canEdit ? (
                            <button onClick={() => clear(it)} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`pricing-clear-${it.id}`}>
                              <RotateCcw className="w-3 h-3"/>Reset
                            </button>
                          ) : <span className="text-xs text-neutral-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
